import React, { useState, useCallback, useEffect } from 'react';
import { Modal, Button, Input, Typography, Table } from '@crisellevaldez/speculo-ui';
import { Organization } from '../../../../types/organization.types';
import { Search, RotateCw, PlusCircle } from 'lucide-react';
import { useCitiesData, CityData } from '../../../../hooks/cities/useCitiesData';
import { useDebouncedSearch } from '../../../../hooks/useDebouncedSearch';
import { OffsetPagination } from '../../../../components/common/OffsetPagination';
import { CreateCityModal } from './CreateCityModal';

interface CityManagementModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedOrg: Organization | undefined;
  onSuccess?: () => void;
}

type Column = {
  key: string;
  header: string;
  isCentered?: boolean;
  cell: (city: CityData) => React.ReactNode;
};

export const CityManagementModal: React.FC<CityManagementModalProps> = ({
  isOpen,
  onClose,
  selectedOrg,
}) => {
  const [searchInput, setSearchInput] = useState('');
  const searchQuery = useDebouncedSearch(searchInput);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);

  const {
    cities,
    isLoading,
    error,
    totalCount,
    refetch,
  } = useCitiesData({
    searchQuery,
    organizationId: selectedOrg?.id,
    offset: currentPage * pageSize,
    pageSize,
  });

  // Reset to the first page whenever the search changes.
  useEffect(() => {
    setCurrentPage(0);
  }, [searchQuery]);

  const changePage = useCallback((page: number) => {
    setCurrentPage(page);
  }, []);

  const handleSearch = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchInput(e.target.value);
  }, []);

  const handleRefresh = useCallback(async () => {
    setSearchInput('');
    setCurrentPage(0);
    await refetch();
  }, [refetch]);

  const columns: Column[] = [
    {
      key: 'name',
      header: 'City Name',
      cell: (city) => `${city.name}, ${city.state}`,
    },
    {
      key: 'coordinates',
      header: 'Coordinates',
      cell: (city) => `${city.coordinates.lat}, ${city.coordinates.lng}`,
    },
  ];

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose} size="2xl">
        <Modal.Header>
          <Typography variant="h3">City Management</Typography>
        </Modal.Header>

        <Modal.Body className="py-8">
          <div className="space-y-6">
            <div className="flex items-center gap-2">
              <div className="flex-1">
                <Input
                  placeholder="Search by name..."
                  startIcon={<Search className="text-gray-400" size={20} />}
                  value={searchInput}
                  onChange={handleSearch}
                  className="w-full"
                />
              </div>
              <Button
                variant="outline"
                size="sm"
                className="p-2 justify-center"
                onClick={handleRefresh}
                disabled={isLoading}
              >
                <RotateCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
              </Button>
              <Button
                variant="primary"
                size="md"
                onClick={() => setShowCreateModal(true)}
                className="flex items-center gap-2 h-9"
              >
                <PlusCircle className="h-4 w-4" />
                New City
              </Button>
            </div>
            <div>
              <div className="h-[300px] 3xl:h-[500px]">
                {error ? (
                  <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700/30 rounded-lg">
                    <p className="text-sm text-red-800 dark:text-red-300">
                      {error instanceof Error ? error.message : 'Failed to load cities'}
                    </p>
                  </div>
                ) : (
                  <Table<CityData>
                    data={cities}
                    columns={columns}
                    keyExtractor={(item) => item.id}
                    emptyStateText="No cities found"
                    loading={isLoading}
                  />
                )}
              </div>
              <OffsetPagination
                currentPage={currentPage}
                totalItems={totalCount}
                pageSize={pageSize}
                onNextPage={() => changePage(currentPage + 1)}
                onPrevPage={() => changePage(currentPage - 1)}
                onPageChange={changePage}
                onPageSizeChange={(size) => {
                  setPageSize(size);
                  setCurrentPage(0);
                }}
                isLoading={isLoading}
                label="cities"
              />
            </div>
          </div>
        </Modal.Body>
      </Modal>

      <CreateCityModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        selectedOrgId={selectedOrg?.id || ''}
        onSuccess={refetch}
      />
    </>
  );
};
